/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
//@ts-nocheck
import React from 'react';
import { Carousel, Image } from 'react-bootstrap';
import { ScrollMenu } from 'react-horizontal-scrolling-menu';
import { useBreakpoint } from '@vlinder-web/breakpoint-hook-widget-react';
import { MapWidget } from '@vlinder-web/map-widget-react';
import { LeftArrow, RightArrow } from './arrows';
import { BlurBackground, getImageStyle } from '../BlurBackground';
import { PdfViewer } from './pdfViewer';
import { ImageOverlay } from './components/ImageOverlay';
import QRCode from 'react-qr-code';

import * as S from './styles';
import { ProtectedImage } from './components/ProtectedImage';

const DEFAULT_TITLE = '';

export interface IMediaCarouselItems {
  type: 'img' | 'pdf' | 'video' | 'map' | 'qr';
  poster?: string;
  src: string;
  title?: string | string[];
  subTitle?: string | string[];
  metaLogo?: string;
  accessToken?: string;
  markers?: any[];
  center?: number[];
}

export interface IMediaCarouselProps {
  data?: IMediaCarouselItems[];
  onCarouselImageClick?(item?: IMediaCarouselItems): void;
  carouselStyles: {
    width: string;
    height: string;
  };
  titleSymbol?: string;
  metaSymbol?: string;
}

const DEFAULT_PDF_POSTER =
  'https://s3.ap-south-1.amazonaws.com/io.vlinder.illustrations/pdf-illustration.png';
const DEFAULT_PLAY_OVERLAY_ICON =
  'https://s3.ap-south-1.amazonaws.com/io.vlinder.icons/play-button.png';
const DEFAULT_AUDIO_OVERLAY_ICON = 'https://i.ibb.co/jfK8zgX/audio.png';
const DEFAULT_PDF_OVERLAY_ICON =
  'https://s3.ap-south-1.amazonaws.com/io.vlinder.icons/pdf.png';
const DEFAULT_VIDEO_POSTER = 'https://i.ibb.co/N3R8JRk/video-camera-png.webp';
const DEFAULT_AUDIO_POSTER = 'https://i.ibb.co/DgcJqCL/audio-poster.png';
const DEFAULT_MAP_POSTER =
  'https://s3.ap-south-1.amazonaws.com/io.vlinder.icons/pdf.png';

function onWheel(apiObj: scrollVisibilityApiType, ev: React.WheelEvent): void {
  const isThouchpad = Math.abs(ev.deltaX) !== 0 || Math.abs(ev.deltaY) < 15;

  if (isThouchpad) {
    ev.stopPropagation();
    return;
  }

  if (ev.deltaY < 0) {
    apiObj.scrollNext();
  } else if (ev.deltaY > 0) {
    apiObj.scrollPrev();
  }
}

export const MediaCarousel = ({
  data,
  carouselStyles,
  titleSymbol,
  metaSymbol,
  onCarouselImageClick,
}: IMediaCarouselProps) => {
  const breakpoint = useBreakpoint();

  const _getImageWidth = () => {
    let _style: any = {
      width: carouselStyles?.width ?? '350px',
    };
    if (breakpoint === 'xs' || breakpoint === 'sm') {
      _style.width = '300px';
    }
    return _style;
  };

  const _getImageHeight = () => {
    let _style: any = {
      height: carouselStyles?.height ?? '200px',
    };
    if (breakpoint === 'xs' || breakpoint === 'sm') {
      _style.height = '200px';
    }
    return _style;
  };

  const _getTitle = (title?: string | string[]) => {
    if (Array.isArray(title) && title?.length > 0) {
      return (
        <>
          {title?.map((ele, index) => {
            if (index === title.length - 1) {
              return <span key={`${ele}-${index}`}>{ele}</span>;
            }
            return (
              <span key={`${ele}-${index}`}>
                {ele} {titleSymbol}{' '}
              </span>
            );
          })}
        </>
      );
    } else return title;
  };

  const _getMeta = (title?: string | string[], metaLogo?: string) => {
    if (Array.isArray(title) && title?.length > 0) {
      return (
        <>
          {title?.map((ele, index) => {
            if (index === title.length - 1) {
              return (
                <span key={`${ele}-${index}`}>
                  {index === 0 && !!metaLogo && (
                    <S.MetaLogo
                      src={metaLogo}
                      className="avatar rounded me-1"
                    />
                  )}
                  {ele}
                </span>
              );
            }
            return (
              <span key={`${ele}-${index}`}>
                {index === 0 && !!metaLogo && (
                  <S.MetaLogo src={metaLogo} className="avatar rounded me-1" />
                )}
                {ele} {metaSymbol}{' '}
              </span>
            );
          })}
        </>
      );
    } else return title;
  };

  const _renderType = (item: IMediaCarouselItems, index: number) => {
    let _comp: any = [];
    if (item?.type === 'img' && item?.src) {
      _comp.push(
        <>
          <BlurBackground
            backgroundImage={item.src}
            width={_getImageWidth().width}
            height={_getImageHeight().height}
            onClick={onCarouselImageClick?.bind(null, item)}
          >
            <ProtectedImage
              key={`prdv3-carousel-item-img-${index}`}
              src={item.src}
              style={{
                ...getImageStyle(),
                objectFit: 'cover',
                height: _getImageHeight().height,
                padding: 0,
              }}
              onClick={onCarouselImageClick?.bind(null, item)}
            />
            {item?.title ?? DEFAULT_TITLE}
          </BlurBackground>
          <S.Title className="fw-bold p-2">{_getTitle(item?.title)} </S.Title>
          <S.Meta className="m-0 px-2">
            {_getMeta(item?.meta, item?.metaLogo)}
          </S.Meta>
        </>
      );
    }
    if (item?.type === 'video' && item?.src) {
      const poster = item?.poster ?? DEFAULT_VIDEO_POSTER;
      _comp.push(
        <>
          <BlurBackground
            backgroundImage={poster}
            width={_getImageWidth().width}
            height={_getImageHeight().height}
            onClick={onCarouselImageClick?.bind(null, item)}
          >
            <ImageOverlay
              key={`prdv3-carousel-item-img-${index}`}
              src={poster}
              overlaySrc={DEFAULT_PLAY_OVERLAY_ICON}
              style={getImageStyle()}
              onClick={onCarouselImageClick?.bind(null, item)}
              imageStyle={{
                objectFit: 'cover',
                height: _getImageHeight().height,
                padding: 0,
              }}
            />
          </BlurBackground>
          <S.Title className="fw-bold p-2">{_getTitle(item?.title)} </S.Title>
          <S.Meta className="m-0 px-2">
            {_getMeta(item?.meta, item?.metaLogo)}
          </S.Meta>
        </>
      );
    }
    if (item?.type === 'audio' && item?.src) {
      const poster = item?.poster ?? DEFAULT_AUDIO_POSTER;
      _comp.push(
        <>
          <BlurBackground
            backgroundImage={poster}
            width={_getImageWidth().width}
            height={_getImageHeight().height}
            onClick={onCarouselImageClick?.bind(null, item)}
          >
            <ImageOverlay
              key={`prdv3-carousel-item-img-${index}`}
              src={poster}
              overlaySrc={DEFAULT_AUDIO_OVERLAY_ICON}
              style={getImageStyle()}
              onClick={onCarouselImageClick?.bind(null, item)}
              imageStyle={{
                objectFit: 'cover',
                height: _getImageHeight().height,
                padding: 0,
              }}
            />
          </BlurBackground>
          <S.Title className="fw-bold p-2">{_getTitle(item?.title)} </S.Title>
          <S.Meta className="m-0 px-2">
            {_getMeta(item?.meta, item?.metaLogo)}
          </S.Meta>
        </>
      );
    }
    if (item?.type === 'pdf') {
      const poster = item?.poster ?? DEFAULT_PDF_POSTER;
      _comp.push(
        <>
          <BlurBackground
            backgroundImage={poster}
            width={_getImageWidth().width}
            height={_getImageHeight().height}
            onClick={onCarouselImageClick?.bind(null, item)}
          >
            <PdfViewer file={item?.src} />
            <ImageOverlay
              key={`prdv3-carousel-item-img-${index}`}
              src={poster}
              overlaySrc={DEFAULT_PDF_OVERLAY_ICON}
              style={getImageStyle()}
              onClick={onCarouselImageClick?.bind(null, item)}
              imageStyle={{
                objectFit: 'cover',
                height: _getImageHeight().height,
                padding: 0,
              }}
            />
          </BlurBackground>
          <S.Title className="fw-bold p-2">{_getTitle(item?.title)} </S.Title>
          <S.Meta className="m-0 px-2">
            {_getMeta(item?.meta, item?.metaLogo)}
          </S.Meta>
        </>
      );
    }
    if (item?.type === 'qr') {
      _comp.push(
        <>
          <BlurBackground
            // backgroundImage={item.src}
            width={_getImageWidth().width}
            height={_getImageHeight().height}
            onClick={onCarouselImageClick?.bind(null, item)}
          >
            <Image
              key={`prdv3-carousel-item-img-${index}`}
              src={item.poster}
              style={{
                ...getImageStyle(),
                objectFit: 'cover',
                height: _getImageHeight().height,
                padding: 0,
              }}
              onClick={onCarouselImageClick?.bind(null, item)}
            />
            {/* <QRCode
                        size={120}
                        style={{
                            height: "auto",
                            maxWidth: "100%",
                            width: "100%"
                        }}
                        value={item?.src}
                        viewBox={`0 0 120 120`}
                        onClick={onCarouselImageClick?.bind(null, item)}
                    /> */}
            {item?.title ?? DEFAULT_TITLE}
          </BlurBackground>
          <S.Title className="fw-bold p-2">{_getTitle(item?.title)} </S.Title>
          <S.Meta className="m-0 px-2">
            {_getMeta(item?.meta, item?.metaLogo)}
          </S.Meta>
        </>
      );
    }
    return _comp;
  };

  return (
    <ScrollMenu LeftArrow={LeftArrow} RightArrow={RightArrow} onWheel={onWheel}>
      {data?.map((item, index) => (
        <div
          key={`pdv3-carousel-item-${index}`}
          style={S.CarouselItemContainer}
        >
          {_renderType(item, index)}
        </div>
      ))}
    </ScrollMenu>
  );
};

MediaCarousel.defaultProps = {
  data: [],
  carouselStyles: {},
  metaSymbol: '•',
  titleSymbol: '|',
};
